import { Component, OnInit, AfterViewInit, HostListener } from "@angular/core";
import { BandsService } from "../bands.service";
import { BandGroup, PriceBandWithHistory } from "../bands-types";
import * as moment from 'moment';
import { HotkeyService } from 'src/app/core/services/hotkey.service';

@Component({
    selector: 'admin-bands',
    templateUrl: './admin-bands.component.html',
    styleUrls: ['./admin-bands.component.scss']
})
export class AdminBandsComponent implements OnInit, AfterViewInit {

    showPriceBandDialog = false;
    newStartDate: Date;
    newRates: number[] = [];
    addError:string = '';
    deleteError:string = '';
    disableAddButton:boolean = false;

    priceBands: PriceBandWithHistory[] = [];
    priceBandGroups: BandGroup[] = [];
    priceBandNames: string[] = [];
    public moment = moment;
    bandGroupToDelete: BandGroup | null = null;
    sortBy: string = 'startDate';
    sortDir: 'asc' | 'desc' = 'asc';

    constructor(private bandsService: BandsService, private hotkeyService: HotkeyService) { }

    ngOnInit(): void {
        this.fetchPriceBands();
    }

    ngAfterViewInit(): void {
        // Register hotkey for adding a new band (Ctrl+Alt+S)
        this.hotkeyService.registerHotkey('ctrl+shift+a', 'Add new band', () => {
            this.openAddDialog();
        });
    }

    // HostListener for Ctrl+Q that cancels the currently open modal.
    @HostListener('document:keydown', ['$event'])
    handleKeydown(event: KeyboardEvent): void {
        if (event.ctrlKey && event.key.toLowerCase() === 'q') {
            event.preventDefault();
            if (this.bandGroupToDelete) {
                this.cancelDelete();
            } else if (this.showPriceBandDialog) {
                this.cancelAddDialog();
            }
        }
    }

    // Function to sort table
    onSort(column: string) {
      if (this.sortBy === column) {
          this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
          this.sortBy = column;
          this.sortDir = 'asc';
      }

      this.sortPriceBands();
    }

    sortPriceBands() {
      this.priceBandGroups.sort((a, b) => {
          let valueA: any;
          let valueB: any;

          if (this.sortBy === 'startDate') {
              valueA = new Date(a.startDate).getTime();
              valueB = new Date(b.startDate).getTime();
          } else if (this.sortBy === 'endDate') {
              valueA = new Date(a.endDate).getTime();
              valueB = new Date(b.endDate).getTime();
          } else {
              valueA = this.getBandValue(a, this.sortBy);
              valueB = this.getBandValue(b, this.sortBy);
          }

          return this.sortDir === 'asc' ? valueA - valueB : valueB - valueA;
      });
    }

    fetchPriceBands() {
        this.bandsService.getPriceBands().subscribe({
            next: (result) => {
                if (result.success)
                {
                    this.priceBandGroups = result.bandGroups;
                    this.priceBandNames = result.bandNames;
                    this.sortPriceBands();
                }
            },
            error: (err) => {
                console.error('Error: ', err);
            }
        });
    }

    cancelAddDialog() {
        this.showPriceBandDialog = false;
    }

    openAddDialog() {
        this.newStartDate = new Date(new Date().getFullYear() + 1, 0,1);
        // initialise newRates to the most recent band rates
        if (this.priceBandGroups.length > 0) {
            this.newRates = this.priceBandGroups[this.priceBandGroups.length - 1].bands.map(b => b.bandRate);
        } else {
            this.newRates = this.priceBandNames.map(b => 0);
        }

        this.addError = '';
        this.showPriceBandDialog = true;
        this.disableAddButton = false;
    }


    addNewBand() {
        this.addError = '';
        this.deleteError = '';
        this.disableAddButton = true;
        let bands = this.priceBandNames.map((name, i) => {
            return { bandName: name, bandRate: this.newRates[i] };
        });
        this.bandsService.addGlobalPriceBands(this.newStartDate, bands).subscribe({
            next: (r) => {
                if (r.success) {
                    this.fetchPriceBands();
                    this.showPriceBandDialog = false;
                } else {
                    this.addError = r.error;
                    this.disableAddButton = false;
                }
            },
            error: (e) => {
                this.addError = e.error.error;
                this.disableAddButton = false;
            }
        });
    };

    formatStartDate(date: Date): string {
        let mom = moment.utc(date);
        return mom.format('DD MMM YYYY');
    }

    formatEndDate(date: Date): string {
        let mom = moment(date);
        if (mom.isSame(moment('9999-12-31'))) {
            return 'no end date';
        }
        mom.subtract(1, 'days');
        return mom.format('DD MMM YYYY');
    }

    getBandValue(group: BandGroup, bandName: string): number {
        let band = group.bands.find(b => b.bandName === bandName);
        return band ? band.bandRate : 0;
    }

    // Called when the user confirms deletion in the modal.
    doDeleteBandGroup() {
        if (this.bandGroupToDelete) {
            this.bandsService.deleteGlobalPriceBands(this.bandGroupToDelete.startDate).subscribe({
                next: (r) => {
                    if (r.success) {
                        this.fetchPriceBands();
                        this.bandGroupToDelete = null;
                    } else {
                        this.deleteError = `Error deleting band group: ${r.error}`;
                    }
                },
                error: (e) => {
                    this.deleteError = `Error deleting band group: ${e.error.error}`;
                    this.bandGroupToDelete = null;
                }
            });
        }
    }

    cancelDelete() {
        this.bandGroupToDelete = null;
    }

    deleteBandGroup(group: BandGroup) {
        this.bandGroupToDelete = group;
    }
}